/**
 * emptyDocDetector — watches a Word document for the "had content → empty"
 * transition that signals the writer has cleared the file to start a new
 * project on top of an existing AsthrDocumentId.
 *
 * The detector is the trigger for the new-project confirmation flow
 * (see NewProjectConfirmModal). It does not act on the transition
 * itself — it just notifies a subscriber.
 *
 * # Mechanism (post-refactor)
 *
 * No ambient ``DocumentSelectionChanged`` subscription. The watcher
 * exposes an explicit ``check()`` method that callers invoke at trigger
 * moments (app mount, run lifecycle transitions, chat-input focus,
 * documentSessionId change). Each check is one cheap ``Word.run``
 * via ``checkDocEmpty`` — first + last paragraph only, constant cost.
 *
 * Internal state tracks whether content has been observed in any prior
 * check so the "had content → empty" transition still fires once.
 *
 * # Empty threshold
 *
 * "Empty" means "the body has a single paragraph whose text is
 * whitespace-only" (see services/wordProbe.ts → ``checkDocEmpty``).
 * Checking only the first paragraph (an earlier shortcut) is unsound:
 * Word docs commonly start with a blank/whitespace paragraph while the
 * rest of the body holds content, and that would false-positive a fully
 * populated doc as empty.
 *
 * # Re-arming
 *
 * The "transition fired" latch resets when the document is observed
 * with content again, so the writer who reverts via undo (and re-fills)
 * gets the prompt fresh on the NEXT clear. There is also an
 * ``acknowledge()`` call returned to the caller — invoke it after
 * resolving the prompt (yes/no) to clear the latch immediately rather
 * than waiting for the next content-rich event.
 */

import { checkDocEmpty } from "./wordProbe";

let _detectorActive = false;

export interface EmptyDocWatcherOptions {
  /** Fired once per had-content → empty transition. */
  onEmpty: () => void;
  /**
   * Tell the watcher the document was known to have content at some
   * point in its history (e.g., the backend session record has a
   * spine_snapshot or run_id). When true, the "have we seen content"
   * gate starts already-tripped, so an already-empty doc on the
   * first check fires the prompt immediately rather than staying silent
   * waiting for content to appear.
   *
   * Default: false (treat first-check-empty as "writer just opened a
   * blank file", don't prompt).
   */
  initialKnownToHaveContent?: boolean;
}

export interface EmptyDocWatcherHandle {
  /**
   * Run one empty-doc check and dispatch ``onEmpty`` if the watcher
   * sees a had-content → empty transition. Safe to call any time.
   *
   * The Promise resolves once the check completes (no awaiting needed
   * for fire-and-forget callers).
   */
  check: () => Promise<void>;
  /** Stop the watcher. Idempotent. Subsequent ``check()`` calls are no-ops. */
  stop: () => void;
  /** Mark the current empty-state as acknowledged so the watcher won't
   *  re-fire ``onEmpty`` until the document has had content again. */
  acknowledge: () => void;
}

/**
 * Create a watcher for the empty transition. Only one watcher should
 * be active at a time; subsequent calls return an inert handle.
 *
 * Unlike the pre-refactor version, this does NOT start any ambient
 * subscription. The caller drives observations explicitly via
 * ``handle.check()``.
 */
// Bounded retry parameters for the "backend says we had content but
// Word reports empty" case. On a cold open, Word.run can return an
// empty body before the saved document has been deserialised into the
// add-in's view. A handful of short retries collapses that race:
// if the doc is genuinely empty (Bug A — the writer cleared it), all
// retries return empty and the prompt fires; if the doc is loading,
// one of the retries observes content and arms the watcher correctly.
const COLD_LOAD_RETRY_COUNT = 4;
const COLD_LOAD_RETRY_DELAY_MS = 500;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function startEmptyDocWatcher(options: EmptyDocWatcherOptions): EmptyDocWatcherHandle {
  let firedForCurrentEmpty = false;
  // ``hasSeenContent`` reflects observations made in THIS watcher's
  // lifetime only. Backend-known-had-content is not evidence the
  // current Word add-in view has loaded — it gates the retry behaviour
  // below instead of pre-confirming content existence.
  let hasSeenContent = false;
  const backendKnownToHaveContent = options.initialKnownToHaveContent === true;
  let firstCheckPending = true;
  let stopped = false;
  if (_detectorActive) {
    return {
      check: async () => undefined,
      stop: () => undefined,
      acknowledge: () => undefined,
    };
  }
  _detectorActive = true;

  const check = async (): Promise<void> => {
    if (stopped) return;
    let { isEmpty } = await checkDocEmpty();
    if (isEmpty === null) return; // Office unavailable — nothing to do.

    // Cold-load race: if this is the watcher's first check and Word
    // reports empty but the backend told us this document had content,
    // retry a few times before believing the empty signal. Word.run
    // can resolve before the saved body is loaded into the add-in's
    // view, which would otherwise fire a spurious new-project prompt
    // on every login.
    if (firstCheckPending) {
      firstCheckPending = false;
      if (isEmpty && backendKnownToHaveContent) {
        for (let i = 0; i < COLD_LOAD_RETRY_COUNT; i++) {
          if (stopped) return;
          await wait(COLD_LOAD_RETRY_DELAY_MS);
          const r = await checkDocEmpty();
          if (r.isEmpty === null) return;
          if (!r.isEmpty) {
            isEmpty = false;
            break;
          }
        }
      }
    }

    if (!isEmpty) {
      hasSeenContent = true;
      firedForCurrentEmpty = false;
      return;
    }
    // Only fire onEmpty if THIS watcher has observed content at some
    // point. Backend state alone is not enough — that's how we end up
    // prompting on a freshly mounted watcher whose Word view hasn't
    // loaded yet.
    if (hasSeenContent && !firedForCurrentEmpty) {
      firedForCurrentEmpty = true;
      try {
        options.onEmpty();
      } catch (err) {
        console.warn("[emptyDocDetector] onEmpty handler threw:", err);
      }
    }
  };

  return {
    check,
    stop: () => {
      if (stopped) return;
      stopped = true;
      _detectorActive = false;
    },
    acknowledge: () => {
      firedForCurrentEmpty = true;
      hasSeenContent = false;
    },
  };
}
